import { Box, Grid, MenuItem, Typography } from "@mui/material";
import { FormTextField, GridCardContent } from "components/atoms";
import { FormContainer, FormContent } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { DialogHelper } from "helpers";
import { useRequest } from "hooks/useRequest";
import { IDeleteByIdRequest, IGetByIdRequest } from "model";
import {
  ICompany,
  ICreateCompanyRequest,
  IUpdateCompanyRequest,
} from "model/Company/ICompany";
import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { CompanyActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { CompanyApiService } from "services";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";

enum EStatus {
  ACTIVE = "A",
  IN_ACTIVE = "I",
}

const validateSchema = yup.object().shape({
  companyName: yup.string().required(translations.yupError.required),
  companyCode: yup.string().required(translations.yupError.required),
  companyAddress: yup.string().required(translations.yupError.required),
  provinceShipTo: yup.string().required(translations.yupError.required),
  provinceSoldTo: yup.string().required(translations.yupError.required),
});

const CustomerDetail = () => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const isAdd = location.pathname.includes("new");
  const action = useAppSelector((state) => state.CompanyReducer.action);
  const [request] = useState<IGetByIdRequest>({
    id: params.id!,
  });
  const { loading, data } = useRequest<IGetByIdRequest, ICompany>(
    request,
    CompanyApiService.getCompanyById
  );

  const createCompanyInitialValues: ICreateCompanyRequest = {
    companyName: "",
    companyCode: "",
    companyAddress: "",
    provinceShipTo: "",
    provinceSoldTo: "",
    description: "",
    status: EStatus.ACTIVE,
  };
  const updateCompanyInitialValues: IUpdateCompanyRequest = {
    ...(data as ICompany),
    status: data?.status ? EStatus.ACTIVE : EStatus.IN_ACTIVE,
  };

  const initialValues = isAdd
    ? createCompanyInitialValues
    : updateCompanyInitialValues;

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: validateSchema,
    onSubmit: (
      values: ICreateCompanyRequest | IUpdateCompanyRequest,
      {
        validateForm,
      }: FormikHelpers<ICreateCompanyRequest | IUpdateCompanyRequest>
    ) => {
      const createRequest: ICreateCompanyRequest = {
        ...(values as ICreateCompanyRequest),
        status: values.status === EStatus.ACTIVE,
      };
      validateForm().then(() => {
        if (isAdd) {
          _createCompany(createRequest);
        } else {
          const updateRequest: IUpdateCompanyRequest = {
            ...(values as IUpdateCompanyRequest),
            status: values.status === EStatus.ACTIVE,
          };
          _updateCompany(updateRequest);
        }
      });
    },
  });
  const createOrUpdateOrDeleteCompanySuccessfully =
    action.includes(CompanyActions.createCompany.successName) ||
    action.includes(CompanyActions.updateCompany.successName) ||
    action.includes(CompanyActions.deleteCompany.successName);

  const getCardTitle = () => {
    return isAdd
      ? I18nTranslator(translations.masterData.addCompany)
      : I18nTranslator(translations.masterData.updateCompany);
  };

  const handleDeleteItem = () => {
    DialogHelper.openConfirmDeleteDialog({
      itemName: data?.companyName,
      onDelete: () => _deleteCompany({ id: data?.id! }),
    });
  };
  const _createCompany = (request: ICreateCompanyRequest) => {
    dispatch(CompanyActions.createCompany.request(request));
  };
  const _updateCompany = (request: IUpdateCompanyRequest) => {
    dispatch(CompanyActions.updateCompany.request(request));
  };

  const _deleteCompany = (request: IDeleteByIdRequest) => {
    dispatch(CompanyActions.deleteCompany.request(request));
  };
  const _clearAction = () => {
    dispatch(CompanyActions.clearAction.request());
  };
  useEffect(() => {
    return () => {
      _clearAction();
    };
  }, []);

  useEffect(() => {
    if (createOrUpdateOrDeleteCompanySuccessfully) {
      _clearAction();
      history.go(-1);
    }
  }, [action]);

  return (
    <GridCardContent>
      <Box sx={{ p: 2, width: "100%" }}>
        <FormContainer
          formik={formik}
          loading={loading || action.includes("_REQUEST")}
          isAdd={isAdd}
          onDelete={handleDeleteItem}
          title={I18nTranslator(getCardTitle())}
        >
          <Typography variant="h3"></Typography>
          <FormGeneralInformation />
        </FormContainer>
      </Box>
    </GridCardContent>
  );
};

const FormGeneralInformation = () => {
  return (
    <FormContent
      title={I18nTranslator(translations.masterData.generaInformation)}
    >
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.companyCode)}
          placeholder={I18nTranslator(translations.placeholder.companyCode)}
          id="companyCode"
          type={"number"}
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.companyName)}
          placeholder={I18nTranslator(translations.placeholder.companyName)}
          id="companyName"
        />
      </Grid>
      <Grid xs={12} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.companyAddress)}
          placeholder={I18nTranslator(translations.placeholder.companyAddress)}
          id="companyAddress"
        />
      </Grid>
      <Grid xs={12} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.provinceSoldTo)}
          placeholder={I18nTranslator(translations.placeholder.provinceSoldTo)}
          id="provinceSoldTo"
        />
      </Grid>
      <Grid xs={12} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.provinceShipTo)}
          placeholder={I18nTranslator(translations.placeholder.provinceShipTo)}
          id="provinceShipTo"
        />
      </Grid>
      <Grid xs={12} item>
        <FormTextField
          label={I18nTranslator(translations.masterData.companyDescription)}
          placeholder={I18nTranslator(
            translations.placeholder.companyDescription
          )}
          id="description"
          multiline
          rows={4}
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(translations.masterData.status)}
          select
          id="status"
        >
          <MenuItem value={EStatus.ACTIVE}>
            {I18nTranslator(translations.masterData.active)}
          </MenuItem>
          <MenuItem value={EStatus.IN_ACTIVE}>
            {I18nTranslator(translations.masterData.inactive)}
          </MenuItem>
        </FormTextField>
      </Grid>
    </FormContent>
  );
};

export default CustomerDetail;
